/**
 * Facebook Testimonials Section - JavaScript Components
 * Handles video playback, slider functionality, and copy button
 */

// ==================================================
// InternalVideo Component
// ==================================================
class InternalVideo extends HTMLElement {
  constructor() {
    super();

    // Elements
    this.playButton = this.querySelector(".internal-video__play");
    this.noPlayBtn = this.dataset.noPlayBtn === "true";
    this.loaded = false;
    this.playing = false;
    this.soundBtn = this.querySelector(".internal-video__sound-btn");
    this.video = this.querySelector("video");
    this.invalidFormatSrc = this.video
      ? this.video.querySelector("source[data-invalid]")
      : null;

    if (this.invalidFormatSrc) {
      this.invalidFormatSrc.remove();
    }

    this.timeline = this.querySelector(".internal-video__timeline");
    this.dragging = false;

    // Event Listeners
    if (this.playButton) {
      this.playButton.addEventListener("click", this.togglePlay.bind(this));
    }

    if (this.soundBtn) {
      this.soundBtn.addEventListener("click", this.toggleMute.bind(this));
    }

    if (this.video) {
      this.video.addEventListener("ended", this.onEnded.bind(this));
      this.video.addEventListener("loadstart", this.onLoadStart.bind(this));
      this.video.addEventListener("canplaythrough", this.onCanPlay.bind(this));
      this.video.addEventListener("play", this.onCanPlay.bind(this));
    }

    if (this.timeline && this.video) {
      this.video.addEventListener("timeupdate", this.updateTimeline.bind(this));
      this.timeline.addEventListener("click", this.seek.bind(this));
      this.timeline.addEventListener("mousedown", this.startDrag.bind(this));
      this.timeline.addEventListener("touchstart", this.startDrag.bind(this));
      document.addEventListener("mouseup", this.stopDrag.bind(this));
      document.addEventListener("touchend", this.stopDrag.bind(this));
      document.addEventListener("mousemove", this.drag.bind(this));
      document.addEventListener("touchmove", this.drag.bind(this));
    }

    // Intersection Observer for autoplay
    if (
      this.dataset.actionOnInactive === "pause" &&
      "IntersectionObserver" in window &&
      this.video
    ) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.05,
      };
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        options
      );
      this.observer.observe(this);
    }
  }

  togglePlay() {
    if (!this.video) return;

    if (this.video.paused) {
      if (!this.loaded) {
        this.video.load();
        this.loaded = true;
      }

      this.classList.add("internal-video--playing");

      if (this.playButton && this.noPlayBtn) {
        this.playButton.style.visibility = "hidden";
      }

      const playPromise = this.video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.classList.add("internal-video--playing");
            if (this.playButton && this.noPlayBtn) {
              this.playButton.style.visibility = "hidden";
            }
          })
          .catch((error) => {
            console.warn("Video play failed:", error);
            this.classList.remove("internal-video--playing");
            if (this.playButton && this.noPlayBtn) {
              this.playButton.style.visibility = "visible";
            }
          });
      }
    } else {
      this.video.pause();
      this.classList.remove("internal-video--playing");
    }
  }

  onEnded() {
    this.classList.remove("internal-video--playing");
  }

  toggleMute() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.classList.toggle("internal-video--muted");
  }

  updateTimeline() {
    if (!this.video) return;
    const percent = (this.video.currentTime / this.video.duration) * 100;
    this.style.setProperty("--completed", percent + "%");
  }

  onCanPlay() {
    this.classList.remove("internal-video--loading");
  }

  startDrag(e) {
    e.preventDefault();
    this.dragging = true;
    this.drag(e);
  }

  stopDrag() {
    this.dragging = false;
  }

  drag(e) {
    if (!this.dragging || !this.video) return;

    if (e.touches) {
      e = e.touches[0];
    }

    this.seek(e);
  }

  seek(e) {
    if (!this.timeline || !this.video) return;
    const rect = this.timeline.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, offsetX / rect.width));
    this.video.currentTime = percent * this.video.duration;
  }

  onLoadStart() {
    this.classList.add("internal-video--loading");
  }

  handleIntersection(entries) {
    if (!this.video) return;

    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // Load video sources
        for (let source of this.video.querySelectorAll("source[data-src]")) {
          source.setAttribute("src", source.getAttribute("data-src"));
          source.removeAttribute("data-src");
        }

        this.video.load();

        const playPromise = this.video.play();

        if (playPromise !== undefined) {
          playPromise
            .catch((error) => {
              if (error.name === "NotAllowedError") {
                this.classList.remove("internal-video--playing");
                if (this.playButton && this.noPlayBtn) {
                  this.playButton.style.visibility = "visible";
                }
              }
            })
            .then(() => {
              if (this.video) {
                this.video.play();
              }
            });
        }

        if (this.observer) {
          this.observer.disconnect();
        }
      }
    });
  }
}

// Register the custom element
if (!customElements.get("internal-video")) {
  customElements.define("internal-video", InternalVideo);
}

// ==================================================
// Splide Component
// ==================================================
class SplideComponent extends HTMLElement {
  connectedCallback() {
    // Wait for DOM to be fully loaded
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    const splideElement = this.querySelector(".splide");
    if (!splideElement) {
      console.warn("Splide element not found");
      return;
    }

    // Wait for Splide library to load
    if (typeof Splide === "undefined") {
      console.warn("Splide library not loaded. Retrying...");
      // Retry after a short delay
      setTimeout(() => this.init(), 100);
      return;
    }

    try {
      // Get settings from data attributes
      const settings = {
        type: this.dataset.type || "slide",
        autoplay: this.dataset.autoplay === "true",
        interval: (parseInt(this.dataset.autoplaySpeed) || 5) * 1000,
        perPage: parseInt(this.dataset.slidesDesktop) || 3,
        perMove: parseInt(this.dataset.perMoveDesktop) || 1,
        gap: (parseInt(this.dataset.gapDesktop) || 24) + "px",
        padding: {
          left: parseInt(this.dataset.sidePaddingDesktop) || 0,
          right: parseInt(this.dataset.sidePaddingDesktop) || 0,
        },
        arrows: !splideElement.classList.contains("splide--destroy-desktop"),
        pagination: !splideElement.classList.contains(
          "splide--destroy-desktop"
        ),
        pauseOnHover: true,
        pauseOnFocus: true,
        resetProgress: false,
        breakpoints: {
          768: {
            perPage: parseInt(this.dataset.slidesMobile) || 1,
            gap: (parseInt(this.dataset.gapMobile) || 15) + "px",
            padding: {
              left: parseInt(this.dataset.sidePaddingMobile) || 15,
              right: parseInt(this.dataset.sidePaddingMobile) || 15,
            },
            destroy: this.dataset.destroyMobile === "true",
          },
          1024: {
            destroy: this.dataset.destroyDesktop === "true",
          },
        },
      };

      // Padding calculation
      if (this.dataset.paddingCalcDesktop === "true") {
        settings.focus = 0;
        settings.omitEnd = true;
      }

      // Initialize Splide
      const splide = new Splide(splideElement, settings);

      // Pause videos when slide changes
      if (this.dataset.pauseVideos === "true") {
        splide.on("move", () => {
          this.querySelectorAll("internal-video").forEach((video) => {
            const videoElement = video.querySelector("video");
            if (videoElement && !videoElement.paused) {
              videoElement.pause();
              video.classList.remove("internal-video--playing");
            }
          });
        });
      }

      // Mount the slider
      splide.mount();

      // Store instance
      this.splide = splide;

      console.log("Splide initialized successfully");
    } catch (error) {
      console.error("Error initializing Splide:", error);
    }
  }

  disconnectedCallback() {
    if (this.splide) {
      try {
        this.splide.destroy();
      } catch (error) {
        console.warn("Error destroying Splide:", error);
      }
    }
  }
}

// Register the custom element
if (!customElements.get("splide-component")) {
  customElements.define("splide-component", SplideComponent);
}

// ==================================================
// Copy Button Component
// ==================================================
class CopyButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.copyToClipboard.bind(this));
  }

  copyToClipboard() {
    const content = this.dataset.content;

    if (!content) {
      console.warn("No content to copy");
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(content)
        .then(() => {
          this.showSuccess();
        })
        .catch((err) => {
          console.error("Failed to copy:", err);
          this.fallbackCopy(content);
        });
    } else {
      this.fallbackCopy(content);
    }
  }

  fallbackCopy(content) {
    const textArea = document.createElement("textarea");
    textArea.value = content;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        this.showSuccess();
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
    }

    document.body.removeChild(textArea);
  }

  showSuccess() {
    this.dataset.success = "true";

    const copyText = this.querySelector(".copy-text");
    const copySuccess = this.querySelector(".copy-success");

    if (copyText) copyText.style.display = "none";
    if (copySuccess) copySuccess.style.display = "block";

    setTimeout(() => {
      this.dataset.success = "false";
      if (copyText) copyText.style.display = "block";
      if (copySuccess) copySuccess.style.display = "none";
    }, 2000);
  }
}

// Register the custom element
if (!customElements.get("copy-button")) {
  customElements.define("copy-button", CopyButton);
}

// Initialize animation on scroll
if ("IntersectionObserver" in window) {
  const animateObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.remove("animate--hidden");
          animateObserver.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    }
  );

  // Observe all sections with animation
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(".animate-section").forEach((section) => {
      animateObserver.observe(section);
    });
  });
}

console.log("Facebook Testimonials components loaded successfully");
